// The mobile app's pitch geometry, ported verbatim.
//
// Every number here is copied from the React Native source, not
// re-derived. That is the whole point of the preview: an author needs to
// see what a learner will see, and a pitch drawn "about right" is worse
// than no preview at all — it would quietly teach that a token sits
// somewhere it does not, and the mistake only surfaces on a phone.
//
// Sources, for when these need to be kept in step:
//   BASE_ZONES        fbk-mobile/constants/academyMockData.ts
//   FORMATION_LAYOUTS fbk-mobile/constants/academyFormations.ts
//   ROLE_LABELS       fbk-mobile/constants/academyFormations.ts
//   PITCH_MARKINGS    fbk-mobile/constants/academyPitchMarkings.ts
//   CHALK             fbk-mobile/components/PitchZoneCard.tsx (SVG viewBox 0 0 100 150)
//
// Coordinate space, identical across all of them: x and y both 0-100,
// y increasing DOWNWARD, portrait, the team attacking UP the screen. The
// attacking third sits at y 4-24 and build-up at y 76-96, so a striker
// belongs near the top and a keeper near the bottom. Inverting this would
// silently flip every lesson.

export type PitchZoneDef = {
  id: string;
  label: string;
  top: number;
  height: number;
  left: number;
  width: number;
};

export const BASE_ZONES: PitchZoneDef[] = [
  { id: "left-wing", label: "Left Wing", top: 4, height: 20, left: 6, width: 28 },
  { id: "central-attack", label: "Central Attack", top: 4, height: 20, left: 34, width: 32 },
  { id: "right-wing", label: "Right Wing", top: 4, height: 20, left: 66, width: 28 },
  { id: "pocket", label: "The Pocket", top: 24, height: 10, left: 6, width: 88 },
  { id: "midfield", label: "Midfield", top: 34, height: 22, left: 6, width: 88 },
  { id: "left-back", label: "Left Back", top: 56, height: 20, left: 6, width: 28 },
  { id: "centre-back", label: "Centre-Back", top: 56, height: 20, left: 34, width: 32 },
  { id: "right-back", label: "Right Back", top: 56, height: 20, left: 66, width: 28 },
  { id: "build-up", label: "Build-Up Zone", top: 76, height: 20, left: 6, width: 88 },
];

export function zoneById(id: string): PitchZoneDef | undefined {
  return BASE_ZONES.find((z) => z.id === id);
}

export function zoneLabel(id: string): string {
  return zoneById(id)?.label ?? id;
}

export type RolePosition = { x: number; y: number };

export const FORMATION_LAYOUTS: Record<string, Record<string, RolePosition>> = {
  "4-3-3": {
    GK: { x: 50, y: 88 },
    LB: { x: 14, y: 66 },
    LCB: { x: 40, y: 68 },
    RCB: { x: 60, y: 68 },
    RB: { x: 86, y: 66 },
    DM: { x: 50, y: 50 },
    LCM: { x: 30, y: 42 },
    RCM: { x: 70, y: 42 },
    LW: { x: 14, y: 16 },
    ST: { x: 50, y: 12 },
    RW: { x: 86, y: 16 },
  },
  "4-2-3-1": {
    GK: { x: 50, y: 88 },
    LB: { x: 14, y: 66 },
    LCB: { x: 40, y: 68 },
    RCB: { x: 60, y: 68 },
    RB: { x: 86, y: 66 },
    LDM: { x: 38, y: 50 },
    RDM: { x: 62, y: 50 },
    LAM: { x: 14, y: 30 },
    CAM: { x: 50, y: 28 },
    RAM: { x: 86, y: 30 },
    ST: { x: 50, y: 12 },
  },
  "3-4-2-1": {
    GK: { x: 50, y: 88 },
    LCB: { x: 30, y: 68 },
    CB: { x: 50, y: 70 },
    RCB: { x: 70, y: 68 },
    LM: { x: 12, y: 44 },
    LCM: { x: 38, y: 48 },
    RCM: { x: 62, y: 48 },
    RM: { x: 88, y: 44 },
    LF: { x: 36, y: 18 },
    RF: { x: 64, y: 18 },
    ST: { x: 50, y: 10 },
  },
};

export const ROLE_LABELS: Record<string, string> = {
  GK: "Goalkeeper",
  LB: "Left Back",
  RB: "Right Back",
  CB: "Centre-Back",
  LCB: "Left Centre-Back",
  RCB: "Right Centre-Back",
  DM: "Holding Midfielder",
  LDM: "Left Holding Mid",
  RDM: "Right Holding Mid",
  LCM: "Left Centre Mid",
  RCM: "Right Centre Mid",
  LM: "Left Midfielder",
  RM: "Right Midfielder",
  LAM: "Left Attacking Mid",
  CAM: "Attacking Midfielder",
  RAM: "Right Attacking Mid",
  LW: "Left Winger",
  RW: "Right Winger",
  LF: "Left Forward",
  RF: "Right Forward",
  ST: "Striker",
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

export function layoutFor(formation: string): Record<string, RolePosition> {
  return FORMATION_LAYOUTS[formation] ?? {};
}

// Where a token lands when a movement sends it to a zone. Nudged below
// the zone's vertical centre so it does not sit on the zone label —
// FormationPitch.zoneDropPoint, same 0.62 factor.
export function zoneDropPoint(zoneId: string): RolePosition | null {
  const zone = zoneById(zoneId);
  if (!zone) return null;
  return {
    x: zone.left + zone.width / 2,
    y: zone.top + zone.height * 0.62,
  };
}

export type MarkingShape = "line-h" | "line-v" | "box" | "circle";

export type PitchMarking = {
  id: string;
  label: string;
  shape: MarkingShape;
  x: number;
  y: number;
  w: number;
  h: number;
  mirrorOf?: string;
};

export const PITCH_MARKINGS: PitchMarking[] = [
  { id: "goal-line-top", label: "Goal line", shape: "line-h", x: 6, y: 1, w: 88, h: 0.6 },
  { id: "goal-line-bottom", label: "Goal line", shape: "line-h", x: 6, y: 98.4, w: 88, h: 0.6, mirrorOf: "goal-line-top" },
  { id: "touchline-left", label: "Touchline", shape: "line-v", x: 5.4, y: 1, w: 0.6, h: 98 },
  { id: "touchline-right", label: "Touchline", shape: "line-v", x: 94, y: 1, w: 0.6, h: 98, mirrorOf: "touchline-left" },
  { id: "halfway-line", label: "Halfway line", shape: "line-h", x: 6, y: 49.7, w: 88, h: 0.6 },
  { id: "penalty-area-top", label: "Penalty area", shape: "box", x: 28, y: 2, w: 44, h: 13.3 },
  { id: "penalty-area-bottom", label: "Penalty area", shape: "box", x: 28, y: 84.7, w: 44, h: 13.3, mirrorOf: "penalty-area-top" },
  { id: "six-yard-box-top", label: "Six-yard box", shape: "box", x: 38, y: 2, w: 24, h: 6 },
  { id: "six-yard-box-bottom", label: "Six-yard box", shape: "box", x: 38, y: 92, w: 24, h: 6, mirrorOf: "six-yard-box-top" },
  { id: "centre-circle", label: "Centre circle", shape: "circle", x: 36, y: 40.67, w: 28, h: 18.67 },
];

export function markingLabel(id: string): string {
  return PITCH_MARKINGS.find((m) => m.id === id)?.label ?? id;
}

// The chalk PitchZoneCard draws in its SVG, in viewBox units (100 x 150).
// Kept in viewBox units rather than converted to percentages so it can be
// compared line-for-line against the source.
export const CHALK = {
  viewBox: "0 0 100 150",
  // Outer touchlines + goal lines, as one rectangle.
  outer: { x: 5.5, y: 1.5, w: 89, h: 147 },
  // A goal is 7.32m across a 68m pitch — 9.6 of these units — drawn
  // into the 1.5 units of margin the boundary leaves outside the goal
  // line. Mirrors ScenePitch, so an author previewing a scene sees
  // the same posts a learner does.
  goals: [
    { x: 45.2, y: 0, w: 9.6, h: 1.5 },
    { x: 45.2, y: 148.5, w: 9.6, h: 1.5 },
  ],
  halfway: { y: 75 },
  centreCircle: { cx: 50, cy: 75, r: 14 },
  boxes: [
    { x: 28, y: 3, w: 44, h: 20 },
    { x: 38, y: 3, w: 24, h: 9 },
    { x: 28, y: 127, w: 44, h: 20 },
    { x: 38, y: 138, w: 24, h: 9 },
  ],
};

// The lesson screen's own palette, so the preview is the same green as
// the phone rather than a dashboard-themed approximation.
export const PITCH_COLOR = {
  // PitchZoneCard.pitchWrap
  zonePitchBg: "#1F6B3E",
  // FormationPitch / DiagramPitch
  boardPitchBg: "#0F2419",
  chalk: "rgba(255,255,255,0.5)",
  zoneBorder: "rgba(255,255,255,0.12)",
  zoneLabel: "rgba(233,255,240,0.78)",
  correct: "#22C55E",
  correctFill: "rgba(34,197,94,0.32)",
  wrong: "#F87171",
  wrongFill: "rgba(248,113,113,0.35)",
  gold: "#FFD700",
  goldFill: "rgba(255,215,0,0.22)",
  goldInk: "#10240F",
  blue: "#38BDF8",
  blueInk: "#06202B",
  red: "#F87171",
  redInk: "#2B0A0A",
  ink: "#F7FAF8",
  inkDim: "rgba(247,250,248,0.65)",
};

// FormationPitch's token size, in px at its reference width. Scaled by
// the preview against the real rendered width so a token keeps its
// proportion at any device size.
export const TOKEN_SIZE_PX = 30;
export const TOKEN_REFERENCE_WIDTH = 330;
